import * as Sentry from '@sentry/react';
import { UnheadProvider, createHead } from '@unhead/react/client';
import React, { useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import Modal from 'react-modal';
import {
  RouterProvider,
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { applyMiddleware, compose, createStore } from 'redux';
import { enableBatching } from 'redux-batched-actions';

import { getQueryClient } from '~/queryClient';
import { setupAxiosInterceptors } from '~/services/axios';
import { getRoutes } from '~/routes';

import intializeAnalytics from './app/analytics';
import { thunk } from './app/middleware';
import rootReducer from './app/modules';
import Provider from './app/provider';
import { RouterContext } from './app/routerContext';

declare global {
  interface Window {
    __PRELOADED_STATE__?: any;
  }
}

const head = createHead();
const queryClient = getQueryClient();

export const renderClientSideApp = () => {
  const preloadedState = window.__PRELOADED_STATE__;
  delete window.__PRELOADED_STATE__;

  const sentryReduxEnhancer = Sentry.createReduxEnhancer();

  const store = createStore(
    // @ts-expect-error
    enableBatching(rootReducer),
    preloadedState,
    compose(applyMiddleware(thunk), sentryReduxEnhancer)
  );

  if (
    process.env.NODE_ENV === 'production' ||
    process.env.NODE_ENV === 'staging'
  ) {
    Sentry.init({
      dsn: 'https://dfb45ef3ceeb9af40d6ce11544eea148@sentry.piczel.tv/4',
      tracesSampleRate: 0.3,
      defaultIntegrations: false,
      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect: useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
          traceXHR: false,
          traceFetch: false,
        }),
        Sentry.browserApiErrorsIntegration({
          setTimeout: true,
          setInterval: true,
          requestAnimationFrame: true,
          XMLHttpRequest: false,
          eventTarget: true,
        }),
        Sentry.breadcrumbsIntegration({
          console: true,
          dom: true,
          fetch: false,
          history: true,
          sentry: true,
          xhr: false,
        }),
      ],
    });
    intializeAnalytics();
  }

  const routes = getRoutes({ queryClient, store });
  const browserRouter = createBrowserRouter(routes);

  setupAxiosInterceptors(store);

  hydrateRoot(
    document.getElementById('root')!,
    <Provider store={store} queryClient={queryClient}>
      <UnheadProvider head={head}>
        {/* @ts-expect-error */}
        <RouterContext.Provider value={browserRouter}>
          <RouterProvider router={browserRouter} />
        </RouterContext.Provider>
      </UnheadProvider>
    </Provider>,
    {
      // Callback called when React catches an error outside of an ErrorBoundary.
      onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
      }),
      // Callback called when React catches an error in an ErrorBoundary.
      onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Caught error', error, errorInfo.componentStack);
      }),
      // Callback called when React automatically recovers from errors.
      onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Recoverable error', error, errorInfo.componentStack);
      }),
    }
  );

  Modal.setAppElement(document.getElementById('root')!);
};

export default renderClientSideApp;
